<template>
  <v-card class="series-overview-card">
    <div class="series-overview-body px-8 pt-8 pb-4">
      <v-container>
        <v-row>
          <v-col cols="9">
            <v-row>
              <v-col cols="12">
                <div
                  class="text-h6 font-weight-bold mb-4 text-secondary report-title"
                >
                  {{ report.title }}
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <div
                  v-for="(gateway, index) in report.gateways"
                  class="gateway-status overview-info-block"
                  :key="gateway.slug"
                  :class="
                    index === report.gateways.length - 1 ? 'mb-0' : 'mb-5'
                  "
                >
                  <div class="text-caption mb-1">
                    {{ gateway.label || 'N/A' }}
                  </div>
                  <div class="d-flex flex-row align-center">
                    <div
                      :class="
                        gateway.status?.slug === 'meets'
                          ? 'status-icon-green'
                          : gateway.status?.slug === 'does-not-meet'
                            ? 'status-icon-red'
                            : gateway.status?.slug === 'partially-meets'
                              ? 'status-icon-orange'
                              : 'status-icon-gray'
                      "
                    />
                    <div class="text-subcaption">
                      {{ gateway.status?.name || 'N/A' }}
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="3">
                <div class="overview-info-block mb-6">
                  <div class="text-caption mb-1">GRADES</div>
                  <div class="text-subcaption">
                    {{ formatGradeDisplay(report.grades) }}
                  </div>
                </div>
                <div class="overview-info-block">
                  <div class="text-caption mb-1">Report Release</div>
                  <div class="text-subcaption">04/24/2024</div>
                </div>
              </v-col>
              <v-col cols="3">
                <div class="overview-info-block mb-6">
                  <div class="text-caption mb-1">Review Version</div>
                  <div class="text-subcaption">v1.5</div>
                </div>
                <div class="overview-info-block">
                  <div class="text-caption mb-1">OER-BASED</div>
                  <div class="text-subcaption">Yes</div>
                </div>
              </v-col>
              <v-col cols="3">
                <div class="overview-info-block mb-6">
                  <div class="text-caption mb-1">
                    Multilingual Learner supports
                  </div>
                  <div class="text-subcaption">Available</div>
                </div>
                <div class="overview-info-block">
                  <div class="text-caption mb-1">Publisher</div>
                  <div class="text-subcaption alt">
                    Smithsonian Science Education Center
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="3" align="right">
            <img
              :src="report.cover.uri"
              :alt="report.cover.alt"
              class="report-cover"
              @error="handleImageError"
            />
          </v-col>
        </v-row>
      </v-container>
    </div>
    <div class="series-overview-footer">
      <v-container>
        <v-row>
          <v-col cols="2.5" class="d-flex align-center">
            <div class="overview-info-block text-no-wrap">
              <div class="text-caption">Subject</div>
              <div class="text-subcaption">
                {{ report.subject.name }}
              </div>
            </div>
          </v-col>
          <v-col cols="2.5" class="d-flex align-center">
            <div class="overview-info-block text-no-wrap">
              <div class="metric-wrapper d-flex flex-row">
                <div class="d-flex flex-column mr-3">
                  <v-img
                    class="format-icon"
                    :width="24"
                    :height="24"
                    src="@/assets/icon-format-1.svg"
                    alt="Format Icon"
                  />
                </div>
                <div class="d-flex flex-column">
                  <div class="text-caption">Format</div>
                  <div class="text-subcaption">
                    {{ report.format.name || 'N/A' }}
                  </div>
                </div>
              </div>
            </div>
          </v-col>
          <v-col cols="2.5" class="d-flex align-center">
            <div class="overview-info-block text-no-wrap">
              <div class="text-caption">foundational skills</div>
              <div class="text-subcaption">98%</div>
            </div>
          </v-col>
          <v-col cols="2.5" class="d-flex align-center">
            <div class="overview-info-block text-no-wrap">
              <div class="text-caption">building knowledge</div>
              <div class="text-subcaption">Not Claimed</div>
            </div>
          </v-col>
          <v-col cols="2.5" class="d-flex align-center">
            <div class="overview-info-block text-no-wrap">
              <div class="text-caption">science of reading</div>
              <div class="text-subcaption">Available</div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </v-card>
</template>

<script setup lang="ts">
  import { mockSeriesOverview } from '../../mocks/api-data';
  import type { Report } from '../../store';
  import { formatGradeDisplay } from '@/lib/grades';

  defineProps<{
    report: Report;
  }>();

  /**
   * Handle image loading errors by attempting to use a fallback image.
   */
  function handleImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    if (!imgElement) return;

    // Try using the cover image from the first mock report as a fallback
    if (
      mockSeriesOverview &&
      mockSeriesOverview.items &&
      mockSeriesOverview.items.length > 0 &&
      mockSeriesOverview.items[0].cover?.uri
    ) {
      imgElement.src = mockSeriesOverview.items[0].cover.uri;
    } else {
      // Fallback to a generic placeholder SVG if mock data is unavailable
      imgElement.src =
        'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAzMDAgNDAwIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2ViZjdmYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiMwMDdlYWIiPlJlcG9ydCBDb3ZlcjwvdGV4dD48L3N2Zz4=';
    }
  }
</script>

<style lang="scss" scoped>
  @use 'vuetify/settings' as settings; // Import settings with namespace
  @use 'sass:map'; // Import sass:map module

  .series-overview-card {
    .overview-info-block {
      .text-caption {
        text-transform: uppercase !important;
      }
      .text-subcaption {
        font-size: 16px;
        font-weight: bold;
        color: rgb(var(--v-theme-secondary));
        &.alt {
          font-size: 12px;
          font-weight: normal;
        }
      }
      &.gateway-status {
        .text-subcaption {
          font-size: 14px;
        }
      }
    }
    .series-overview-body {
      .report-cover {
        max-width: 232px;
        display: block;
        margin-left: auto;
      }
      .report-title {
        font-family: 'rockwell', sans-serif;
        font-size: 48px !important;
        font-weight: normal !important;
      }
    }
    .series-overview-footer {
      background-color: #f0f3f5;
      .v-col {
        .overview-info-block {
          width: 100%;
          border-right: 1px solid rgba(var(--v-theme-blue), 0.25);
        }
        &:last-child {
          .overview-info-block {
            border-right: none;
          }
        }
      }
      // Metric Wrapper
      .metric-wrapper {
        .text-caption {
          text-transform: uppercase !important;
        }
      }
    }
  }

  // Mobile specific adjustments (sm and down)
  @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
    // Re-apply cols="12" behavior via CSS for mobile since template was reverted
    .series-overview-body .v-row > .v-col,
    .series-overview-footer .v-row > .v-col {
      flex-basis: 100%;
      max-width: 100%;
      width: 100%;
    }
    // Ensure image comes after text on mobile
    .series-overview-body .v-row > .v-col[align='right'] {
      order: 1;
    }

    .series-overview-body {
      .overview-info-block {
        margin-bottom: 1.5rem !important; // Consistent spacing for stacked info blocks, !important needed to override mb-6/mb-5
      }
      .v-col {
        padding-bottom: 0; // Reduce default column padding when stacked
      }
      // Adjust image alignment for mobile
      .report-cover {
        margin-left: auto;
        margin-right: auto; // Re-center image on mobile
      }
    }
    .series-overview-footer {
      .v-col {
        padding-top: 0.75rem; // Add padding between stacked footer items
        padding-bottom: 0.75rem;
        .overview-info-block {
          border-right: none; // Remove side border
          border-bottom: 1px solid rgba(var(--v-theme-blue), 0.25); // Add bottom border
          padding-bottom: 0.75rem; // Spacing before border
          width: 100%;
        }
        &:last-child {
          .overview-info-block {
            border-bottom: none; // No border on the last item
            padding-bottom: 0;
          }
        }
      }
    }
  }
</style>
